import { AdmissionResultRowDTO } from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import { GOAL_STATUS_TYPES } from '@shared/constants';
import { GoalStatusTypes } from '@shared/enums';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';

export const OFFER_STATUSES = [
  GoalStatusTypes.OfferReceived,
  GoalStatusTypes.OfferAccepted,
  GoalStatusTypes.OfferDeclined,
];

export type OutcomeFilter = 'admitted' | 'accepted' | 'rejected' | 'all';

export const OUTCOME_OPTIONS: { value: OutcomeFilter; label: string }[] = [
  { value: 'admitted', label: 'Admitted (any offer)' },
  { value: 'accepted', label: 'Offer accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All results' },
];

export type CycleYear = number | 'all';

export const cycleYearOf = (row: AdmissionResultRowDTO) =>
  row.graduationYear ?? (row.resultDate ? moment(row.resultDate).year() : undefined);

export const rowsInCycle = (rows: AdmissionResultRowDTO[], year: CycleYear) =>
  year === 'all' ? rows : rows.filter((row) => cycleYearOf(row) === year);

export const availableYears = (rows: AdmissionResultRowDTO[]) =>
  [...new Set(rows.map(cycleYearOf).filter((year): year is number => !!year))].sort(
    (a, b) => b - a
  );

export type Filters = {
  outcome: OutcomeFilter;
  countries: string[];
  universities: string[];
  mentors: string[];
  verifiedOnly: boolean;
};

export const isOffer = (row: AdmissionResultRowDTO) => OFFER_STATUSES.includes(row.status);

const matchesOutcome = (row: AdmissionResultRowDTO, outcome: OutcomeFilter) => {
  if (outcome === 'admitted') return isOffer(row);
  if (outcome === 'accepted') return row.status === GoalStatusTypes.OfferAccepted;
  if (outcome === 'rejected') return row.status === GoalStatusTypes.ApplicationRejected;
  return true;
};

export const applyContextFilters = (rows: AdmissionResultRowDTO[], filters: Filters) =>
  rows.filter(
    (row) =>
      (!filters.countries.length || filters.countries.includes(row.country)) &&
      (!filters.universities.length || filters.universities.includes(row.universityId)) &&
      (!filters.mentors.length ||
        row.mentors.some((mentor) => filters.mentors.includes(mentor.id))) &&
      (!filters.verifiedOnly || row.hasAdmissionDocument)
  );

export const applyOutcomeFilter = (rows: AdmissionResultRowDTO[], outcome: OutcomeFilter) =>
  rows.filter((row) => matchesOutcome(row, outcome));

export const computeSummary = (contextRows: AdmissionResultRowDTO[]) => {
  const offers = contextRows.filter(isOffer);
  const rejections = contextRows.filter(
    (row) => row.status === GoalStatusTypes.ApplicationRejected
  );
  const decided = offers.length + rejections.length;

  return {
    admittedStudents: new Set(offers.map((row) => row.studentId)).size,
    offers: offers.length,
    offersAccepted: offers.filter((row) => row.status === GoalStatusTypes.OfferAccepted).length,
    universities: new Set(offers.map((row) => row.universityId)).size,
    countries: new Set(offers.map((row) => row.country).filter(Boolean)).size,
    acceptanceRate: decided ? Math.round((offers.length / decided) * 100) : null,
  };
};

export const groupByUniversity = (rows: AdmissionResultRowDTO[]) => {
  const groups = new Map<
    string,
    { id: string; name: string; country: string; results: number; students: Set<string> }
  >();

  for (const row of rows) {
    const group = groups.get(row.universityId) ?? {
      id: row.universityId,
      name: row.universityName,
      country: row.country,
      results: 0,
      students: new Set<string>(),
    };
    group.results += 1;
    group.students.add(row.studentId);
    groups.set(row.universityId, group);
  }

  return [...groups.values()]
    .map((group) => ({ ...group, students: group.students.size }))
    .sort((a, b) => b.students - a.students || a.name.localeCompare(b.name));
};

export const groupByMentor = (rows: AdmissionResultRowDTO[]) => {
  const groups = new Map<
    string,
    { id: string; name: string; results: number; students: Set<string>; courses: Set<string> }
  >();

  for (const row of rows) {
    for (const mentor of row.mentors) {
      const group = groups.get(mentor.id) ?? {
        id: mentor.id,
        name: mentor.name,
        results: 0,
        students: new Set<string>(),
        courses: new Set<string>(),
      };
      group.results += 1;
      group.students.add(row.studentId);
      mentor.courses.forEach((course) => group.courses.add(course));
      groups.set(mentor.id, group);
    }
  }

  return [...groups.values()]
    .map((group) => ({ ...group, students: group.students.size, courses: [...group.courses] }))
    .sort((a, b) => b.students - a.students || a.name.localeCompare(b.name));
};

export const groupByCountry = (rows: AdmissionResultRowDTO[]) => {
  const groups = new Map<string, { country: string; results: number; students: Set<string> }>();

  for (const row of rows) {
    const key = row.country || 'Unknown';
    const group = groups.get(key) ?? { country: key, results: 0, students: new Set<string>() };
    group.results += 1;
    group.students.add(row.studentId);
    groups.set(key, group);
  }

  return [...groups.values()]
    .map((group) => ({ ...group, students: group.students.size }))
    .sort((a, b) => b.students - a.students);
};

export const groupByPeriod = (rows: AdmissionResultRowDTO[], byMonth: boolean) => {
  const groups = new Map<
    string,
    { key: string; label: string; results: number; students: Set<string> }
  >();

  for (const row of rows) {
    const date = row.resultDate ? moment(row.resultDate) : null;
    const cycle = cycleYearOf(row);
    const key = byMonth
      ? date
        ? date.format('YYYY-MM')
        : 'no-date'
      : cycle
      ? String(cycle)
      : 'no-cycle';
    const label = byMonth
      ? date
        ? date.format('MMMM YYYY')
        : 'No date'
      : cycle
      ? `Class of ${cycle}`
      : 'No graduation year';
    const group = groups.get(key) ?? { key, label, results: 0, students: new Set<string>() };
    group.results += 1;
    group.students.add(row.studentId);
    groups.set(key, group);
  }

  return [...groups.values()]
    .map((group) => ({ ...group, students: group.students.size }))
    .sort((a, b) => b.key.localeCompare(a.key));
};

export const statusLabel = (status: number) => GOAL_STATUS_TYPES[status] ?? 'Unknown';

export const formatResultDate = (row: AdmissionResultRowDTO) => {
  if (!row.resultDate) return 'No date';

  const formatted = moment(row.resultDate).format('DD MMM YYYY');

  return row.resultDateSource === 'last-update' ? `~ ${formatted}` : formatted;
};

export const buildResultsCsv = (rows: AdmissionResultRowDTO[]) =>
  stringify([
    [
      'Student',
      'Graduation Year',
      'University',
      'Country',
      'Majors',
      'Degree',
      'Result',
      'Result Date',
      'Date Source',
      'Admission Document',
      'Mentors (sessions before the result)',
      'Courses',
    ],
    ...rows.map((row) => [
      row.studentName,
      row.graduationYear ?? '',
      row.universityName,
      row.country,
      row.majors.join('; '),
      row.degree ?? '',
      statusLabel(row.status),
      row.resultDate ? moment(row.resultDate).format('YYYY-MM-DD') : '',
      row.resultDateSource === 'results-date'
        ? 'Results date'
        : row.resultDateSource === 'last-update'
        ? 'Last update (estimated)'
        : '',
      row.hasAdmissionDocument ? 'Yes' : 'No',
      row.mentors.map((mentor) => `${mentor.name} (${mentor.sessions})`).join('; '),
      [...new Set(row.mentors.flatMap((mentor) => mentor.courses))].join('; '),
    ]),
  ]);
